import { useState, useRef, useEffect } from "react";
import { X, Send, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { SOCIALS, WHATSAPP_LINK } from "@/data/socials";

const LOGO = "/assistant-logo.png";
const LEAD_TAG = "[[LEAD_FORM]]";

const SUGGESTIONS = [
  "What courses do you offer?",
  "Explain the MAEE framework",
  "Which market tools can I use?",
  "How do I enroll?",
];

function uid() {
  try { return crypto.randomUUID(); } catch { return `s-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

function Logo({ className }) {
  return <img src={LOGO} alt="KRV Prime" className={className} />;
}

function Dot({ d = "0" }) {
  return <span className="h-1.5 w-1.5 rounded-full bg-slate-300 inline-block animate-bounce" style={{ animationDelay: `${d}ms` }} />;
}

function LeadForm({ sessionId, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_id: sessionId, interest: "Enrolment / callback via Assistant" }),
      });
      toast.success("Thanks! Our team will reach out shortly.");
      onDone(form.name);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-gold/40 rounded-lg p-3 space-y-2 shadow-sm" data-testid="assistant-lead-form">
      <p className="text-xs font-semibold text-navy">Share your details and our team will reach out:</p>
      <input required placeholder="Your name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="lead-name" className="w-full h-9 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
      <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="lead-email" className="w-full h-9 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
      <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} data-testid="lead-phone" className="w-full h-9 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
      <button type="submit" disabled={busy} data-testid="lead-submit" className="w-full h-9 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors disabled:opacity-60">
        {busy ? "Submitting…" : "Request a callback"}
      </button>
    </form>
  );
}

export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const sessionRef = useRef(uid());
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, busy, showLead]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setShowLead(false);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setBusy(true);
    let full = "";
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          try {
            const d = JSON.parse(line);
            if (d.delta) {
              full += d.delta;
              const shown = full.replace(LEAD_TAG, "").trimEnd();
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", text: shown };
                return c;
              });
            }
          } catch { /* partial */ }
        }
      }
      if (full.includes(LEAD_TAG)) setShowLead(true);
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Sorry, I couldn't respond right now. Please try again, or reach us via the Contact page." };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  const onLeadDone = (name) => {
    setShowLead(false);
    setMessages((m) => [...m, { role: "assistant", text: `Thanks${name ? ", " + name : ""}! Our team will reach out shortly. Meanwhile, feel free to explore our Courses page.` }]);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="assistant-toggle" aria-label="Open KRV Prime Assistant"
          className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full bg-white border border-border shadow-2xl shadow-navy/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform p-2.5">
          <Logo className="h-full w-full object-contain" />
          <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-white" />
        </button>
      )}

      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-6 right-6 z-50 w-[min(92vw,380px)] h-[min(72vh,560px)] flex flex-col rounded-2xl overflow-hidden bg-white border border-border shadow-2xl shadow-navy/30 animate-fade-up">
          <div className="bg-navy grain px-4 py-3 flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0 p-1.5"><Logo className="h-full w-full object-contain" /></span>
            <div className="flex-1">
              <p className="font-display font-bold text-white text-sm leading-tight">KRV Prime Assistant</p>
              <p className="text-[11px] text-slate-300 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" /> Online · replies instantly</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Close" className="text-slate-300 hover:text-white transition-colors"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex items-stretch border-b border-gold/30">
            <button onClick={() => setShowLead(true)} data-testid="assistant-talk-btn" className="flex-1 flex items-center justify-center gap-2 bg-gold/10 text-navy text-sm font-semibold py-2.5 hover:bg-gold/20 transition-colors">
              <Phone className="h-4 w-4 text-gold" /> Talk to us / Enroll
            </button>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="assistant-whatsapp-btn" aria-label="Chat on WhatsApp" className="flex items-center justify-center gap-2 px-4 bg-[#25D366] text-white text-sm font-semibold hover:brightness-95 transition-all">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-subtle">
            <div className="flex gap-2 items-start" data-testid="assistant-greeting">
              <span className="h-7 w-7 rounded-full bg-white border border-border flex items-center justify-center shrink-0 mt-0.5 p-1"><Logo className="h-full w-full object-contain" /></span>
              <div className="bg-white border border-border rounded-lg rounded-tl-none px-3 py-2 text-sm text-slate-700 leading-relaxed">
                Hi! I'm the KRV Prime Assistant. Ask me about our courses, market tools, research approach, the MAEE framework or our books — how can I help you learn today?
              </div>
            </div>

            {messages.length === 0 && (
              <div data-testid="assistant-suggestions">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 mt-3">Try asking</p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`assistant-suggestion-${i}`} className="block w-full text-left text-sm text-navy hover:text-gold transition-colors">· {s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-message-${i}`} className={`flex gap-2 items-start ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <span className="h-7 w-7 rounded-full bg-white border border-border flex items-center justify-center shrink-0 mt-0.5 p-1"><Logo className="h-full w-full object-contain" /></span>}
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-navy text-white rounded-tr-none" : "bg-white border border-border text-slate-700 rounded-tl-none"}`}>
                  {m.text || (busy && i === messages.length - 1 ? <span className="inline-flex gap-1"><Dot /><Dot d="150" /><Dot d="300" /></span> : "")}
                </div>
              </div>
            ))}

            {showLead && (
              <div className="flex gap-2 items-start">
                <span className="h-7 w-7 rounded-full bg-white border border-border flex items-center justify-center shrink-0 mt-0.5 p-1"><Logo className="h-full w-full object-contain" /></span>
                <div className="flex-1"><LeadForm sessionId={sessionRef.current} onDone={onLeadDone} /></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-border px-3 pt-2 pb-1 flex items-center justify-center gap-2 bg-white" data-testid="assistant-socials">
            <span className="text-[11px] text-slate-400 mr-1">Follow us</span>
            {SOCIALS.filter((s) => s.href).map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`assistant-social-${s.label.toLowerCase()}`} aria-label={s.label} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-gold transition-colors">
                <s.icon className="h-4 w-4" />
              </a>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-2.5 flex items-center gap-2 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message…" data-testid="assistant-input"
              className="flex-1 h-10 rounded-full border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="assistant-send" aria-label="Send" className="h-10 w-10 shrink-0 rounded-full bg-gold text-navy flex items-center justify-center hover:bg-gold-soft transition-colors disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
